// src/components/UploadTable.tsx
import React, { useState } from "react";
import UploadField from "../../components/UploadField";
import AttachmentEmptyState from "./AttachmentEmptyState";

type UploadTableProps = {
    title?: string;
    values: string[];
    onChange: (index: number, url: string) => void;
    add: (url: string) => void;
    remove: (index: number) => void;
    readOnly?: boolean;
};

const UploadTable: React.FC<UploadTableProps> = ({
    title,
    values,
    onChange,
    add,
    remove,
    readOnly
}) => {

    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const cleanedValues = values.filter(
        (v): v is string => typeof v === "string" && v.trim() !== ""
    );

    if (readOnly && cleanedValues.length === 0 && !pendingFile) {
        return <AttachmentEmptyState multiple />;
    }

    const handleAddClick = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "application/pdf,image/*";

        input.onchange = (e: any) => {
            const picked = e.target.files?.[0];
            if (!picked) return;
            setPendingFile(picked);  // mount temp UploadField
        };

        input.click();
    };

    return (
        <div className="border rounded-lg p-3 bg-white shadow-sm">

            {/* {title && <p className="font-semibold text-xs mb-2">{title}</p>} */}

            {/* Temporary hidden UploadField JUST for Add button uploads */}
            {pendingFile && (
                <div className="hidden">
                    <UploadField
                        label=""
                        value=""
                        mode="file"
                        initialFile={pendingFile}   // <— SEND FILE IN
                        onChange={(url: string) => {
                            add(url);
                            setPendingFile(null);
                        }}
                    />
                </div>
            )}

            {/* Header */}
            <div className="grid grid-cols-3 text-[10px] font-semibold border-b pb-2">
                <span>File Name</span>
                <span className="text-center">View File</span>
                <span className="text-center">Remove File</span>
            </div>


            {/* Rows */}
            {cleanedValues.map((url, i) => {
                if (!url) return null; // skips "", null, undefined

                return (
                    <div
                        key={i}
                        className="grid grid-cols-3 items-center py-2 border-b last:border-b-0"
                    >
                        <span className="text-[10px]">Document #{i + 1}</span>

                        <div className="flex justify-center">
                            <a
                                href={url}
                                target="_blank"
                                className="border p-1 rounded-md"
                            >
                                ↗
                            </a>
                        </div>

                        <div className="flex justify-center">
                            {!readOnly ? (
                                <button
                                    onClick={() => remove(i)}
                                    className="border p-1 rounded-md text-xs"
                                >
                                    🗑
                                </button>
                            ) : (
                                <span className="text-text-subtle">—</span>
                            )}
                        </div>

                        <div className="col-span-3 mt-2">
                            <UploadField
                                label=""
                                value={url}
                                onChange={(newUrl: string) => onChange(i, newUrl)}
                                mode="file"
                                accept="application/pdf,image/*"
                                forceUploadOnly={true}
                            />
                        </div>
                    </div>
                );
            })}


            {/* Loader */}

            {pendingFile && (
                <div className="grid grid-cols-3 items-center py-2 border-b animate-pulse">

                    <span className="text-[10px] text-text-subtle">Uploading…</span>

                    <div className="flex justify-center">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>

                    <div className="flex justify-center">
                        <span className="text-text-subtle text-[10px]">—</span>
                    </div>
                </div>
            )}


            {/* Add button */}
            {/* Add button */}
            {!readOnly && (
                <div className="flex justify-center items-center">
                    <button
                        onClick={handleAddClick}
                        disabled={!!pendingFile}
                        className={`mt-3 w-full px-3 font-bold border rounded-md py-2 text-[10px]
                ${pendingFile
                                ? "bg-bg-button-primary/60 cursor-not-allowed"
                                : "bg-bg-button-primary"
                            }`}
                    >
                        {pendingFile
                            ? "UPLOADING..."
                            : cleanedValues.length > 0
                                ? "+ UPLOAD ADDITIONAL FILES"
                                : "+ UPLOAD FILE"}
                    </button>
                </div>
            )}

        </div>
    );
};

export default UploadTable;
